import React, { useEffect } from 'react'
import {
  Box,
  Typography,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemText,
} from '@mui/material'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import { useDispatch, useSelector } from 'react-redux'
import { getAllProgramRequirements } from '@/actions/admin'

import Loader from '../../components/layout/Loader'
import ProgramRequirementsList from '../../components/Program/ProgramRequirementsList'
import ProgramVisibilityList from '../../components/Program/ProgramVisibilityList'
import StateActionMapping from '../../components/Program/StateActionMapping'
import ActionsList from '../../components/Program/ActionsList'
import RewardProgramSummary from '../../components/Program/RewardProgramSummary'
import ProgramSection from '../../components/Program/ProgramSection'

export default function ProgramOverview() {
  const dispatch = useDispatch()
  const allProgramRequirements = useSelector(
    (state) => state.admin.allProgramRequirements,
  )
  const isLoading = useSelector((state) => state.admin.isLoading)

  useEffect(() => {
    if (!allProgramRequirements) {
      dispatch(getAllProgramRequirements())
    }
  }, [dispatch, allProgramRequirements])

  if (isLoading) return <Loader />
  if (!allProgramRequirements) return null

  // -----------------------------------------------
  // Sort programs alphabetically
  // -----------------------------------------------
  allProgramRequirements.sort((a, b) =>
    a.utility_program.name.localeCompare(b.utility_program.name),
  )

  // -----------------------------------------------
  // Build requirement → { programs, sections } mapping
  // -----------------------------------------------
  const requirementMap = {}

  function addRequirement(requirement, program, section, device) {
    if (!requirement) return

    const key = `${requirement.name}|${requirement.description}`

    if (!requirementMap[key]) {
      requirementMap[key] = {
        requirement,
        device,
        active_programs: new Set(),
        inactive_programs: new Set(),
        sections: new Set(),
      }
    }
    const name = program.utility_program.name
    if (program.utility_program.active) {
      requirementMap[key].active_programs.add(name)
    } else {
      requirementMap[key].inactive_programs.add(name)
    }
    requirementMap[key].sections.add(section + (device ? ` (${device})` : ''))
  }

  for (const program of allProgramRequirements) {
    // Eligibility
    for (const device of Object.keys(
      program.program_eligibility_requirements,
    )) {
      for (const requirement of program.program_eligibility_requirements[device]
        .requirements) {
        addRequirement(requirement, program, `Eligibility`, device)
      }
    }
    // Participation
    for (const device of Object.keys(
      program.program_participation_requirements,
    )) {
      for (const requirement of program.program_participation_requirements[
        device
      ].requirements) {
        addRequirement(requirement, program, `Participation`, device)
      }
    }
    // Visibility
    for (const requirement of program.visible_program_rules?.requirements ||
      []) {
      addRequirement(requirement, program, 'Visibility')
    }
  }

  // Convert to sorted array:
  const sortedRequirements = Object.values(requirementMap)
    .map((entry) => ({
      requirement: entry.requirement,
      device: entry.device,
      activeProgramList: Array.from(entry.active_programs).sort(),
      inactiveProgramList: Array.from(entry.inactive_programs).sort(),
      sectionList: Array.from(entry.sections).sort(),
      count: entry.active_programs.size + entry.inactive_programs.size,
    }))
    .sort((a, b) => b.count - a.count) // sort descending

  // -----------------------------------------------
  // RENDER
  // -----------------------------------------------
  return (
    <Box p={3}>
      <Typography variant="h4" gutterBottom>
        Program Debugger
      </Typography>

      <Box mt={3}>
        {/* ========================================================= */}
        {/* =============== ALL REQUIREMENTS SECTION =============== */}
        {/* ========================================================= */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h5">
              All Requirements (Across Programs)
            </Typography>
          </AccordionSummary>

          <AccordionDetails>
            <List sx={{ width: '100%' }}>
              {sortedRequirements.map((entry, index) => (
                <Accordion key={index} sx={{ mb: 2 }}>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {entry.requirement.name}
                    </Typography>
                  </AccordionSummary>

                  <AccordionDetails>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {entry.requirement.description}
                    </Typography>

                    <Typography variant="body2">
                      <strong>Active Programs:</strong>{' '}
                      {entry.activeProgramList.join(', ') || '—'}
                    </Typography>

                    <Typography variant="body2">
                      <strong>Inactive Programs:</strong>{' '}
                      {entry.inactiveProgramList.join(', ') || '—'}
                    </Typography>

                    <Typography variant="body2">
                      <strong>Sections:</strong> {entry.sectionList.join(', ')}
                    </Typography>

                    <Typography variant="body2">
                      <strong>Programs Count:</strong> {entry.count}
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              ))}
            </List>
          </AccordionDetails>
        </Accordion>

        {/* ========================================================= */}
        {/* ====================== ALL PROGRAMS ===================== */}
        {/* ========================================================= */}
        <Accordion sx={{ mt: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h5">All Programs</Typography>
          </AccordionSummary>

          <AccordionDetails>
            {allProgramRequirements.map((program) => (
              <Accordion key={program.utility_program.id} sx={{ mt: 2 }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography variant="h6">
                    Program: {program.utility_program.name}
                  </Typography>
                </AccordionSummary>

                <AccordionDetails>
                  <ProgramSection
                    title="Program Eligibility Rules"
                    children={
                      <ProgramRequirementsList
                        requirements={program.program_eligibility_requirements}
                      />
                    }
                  />

                  <ProgramSection
                    title="Program Participation Rules"
                    children={
                      <ProgramRequirementsList
                        requirements={
                          program.program_participation_requirements
                        }
                      />
                    }
                  />

                  <ProgramSection
                    title="Visibility Rules"
                    children={
                      <ProgramVisibilityList
                        requirements={
                          program.visible_program_rules?.requirements
                        }
                      />
                    }
                  />

                  <ProgramSection
                    title="State Action Mapping (Verification)"
                    children={
                      <StateActionMapping
                        requirements={program.actions_mapping}
                      />
                    }
                  />

                  <ProgramSection
                    title="Enrolled Actions"
                    children={
                      <ActionsList
                        actions={program.device_enrollment_actions}
                      />
                    }
                  />

                  <ProgramSection
                    title="Unenrolled Actions"
                    children={
                      <ActionsList
                        actions={program.device_unenrollment_actions}
                      />
                    }
                  />

                  <ProgramSection
                    title="Reward Programs"
                    children={
                      <RewardProgramSummary
                        rewardPrograms={program.reward_programs}
                      />
                    }
                  />

                  <ProgramSection
                    title="Other Program Configurations"
                    children={
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, overflowX: 'auto' }}
                      >
                        <pre>
                          {JSON.stringify(program.other_configuration, null, 2)}
                        </pre>
                      </Paper>
                    }
                  />

                  <ProgramSection
                    title="Raw Program Config (JSON)"
                    children={
                      <Paper
                        variant="outlined"
                        sx={{ p: 2, overflowX: 'auto' }}
                      >
                        <pre>
                          {JSON.stringify(program.program_config, null, 2)}
                        </pre>
                      </Paper>
                    }
                  />
                </AccordionDetails>
              </Accordion>
            ))}
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  )
}
